import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, X } from "lucide-react";
import { useUpdateMilestone } from "@/hooks/useProjects";
import type { ProjectFull, ProjectMilestone } from "@/hooks/useProjects";

const DAY_W = 22;
const ROW_H = 56;
const LABEL_W = 320;
const HEADER_YEAR_H = 28;
const HEADER_MONTH_H = 36;
const HEADER_H = HEADER_YEAR_H + HEADER_MONTH_H;
const VIEWPORT_MAX = 720; // max body height (px) before internal vertical scroll kicks in

export function GanttView({ project, clientFacing = false }: { project: ProjectFull; clientFacing?: boolean }) {
  const { milestones, teamMembers } = project;
  const [editingId, setEditingId] = useState<string | null>(null);
  const [localProg, setLocalProg] = useState<number | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const updateMs = useUpdateMilestone(project.id);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrollLeft = useRef(0);
  const isProgrammaticScroll = useRef(false);
  const programmaticTimer = useRef<number | null>(null);

  // ───── Compute timeline range ─────
  const { rangeStart, rangeEnd, totalDays, today, sortedMs } = useMemo(() => {
    const sorted = [...milestones].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const all: number[] = [];
    if (project.startDate) all.push(+new Date(project.startDate));
    for (const m of sorted) {
      if (m.startDate) all.push(+new Date(m.startDate));
      if (m.dueDate) all.push(+new Date(m.dueDate));
    }
    let start = all.length ? Math.min(...all) : Date.now();
    let end = all.length ? Math.max(...all) : Date.now();
    start -= 7 * 86400_000;
    end += 14 * 86400_000;
    const sd = new Date(start); sd.setUTCDate(1); sd.setUTCHours(0, 0, 0, 0);
    const ed = new Date(end); ed.setUTCMonth(ed.getUTCMonth() + 1); ed.setUTCDate(0); ed.setUTCHours(0, 0, 0, 0);
    const totalDays = Math.max(1, Math.round((+ed - +sd) / 86400_000));
    return { rangeStart: sd, rangeEnd: ed, totalDays, today: new Date(), sortedMs: sorted };
  }, [milestones, project.startDate]);

  const totalW = totalDays * DAY_W;

  // ───── Month + year headers ─────
  const months = useMemo(() => {
    const out: { label: string; year: number; startX: number; widthPx: number }[] = [];
    const cur = new Date(rangeStart);
    while (cur < rangeEnd) {
      const mStart = new Date(cur);
      const mEnd = new Date(cur); mEnd.setUTCMonth(mEnd.getUTCMonth() + 1);
      const startDay = Math.round((+mStart - +rangeStart) / 86400_000);
      const endDay = Math.min(totalDays, Math.round((+mEnd - +rangeStart) / 86400_000));
      out.push({
        label: mStart.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" }),
        year: mStart.getUTCFullYear(),
        startX: startDay * DAY_W,
        widthPx: (endDay - startDay) * DAY_W,
      });
      cur.setUTCMonth(cur.getUTCMonth() + 1);
    }
    return out;
  }, [rangeStart, rangeEnd, totalDays]);

  const yearBands = useMemo(() => {
    const bands: { year: number; startX: number; widthPx: number }[] = [];
    for (const mo of months) {
      const last = bands[bands.length - 1];
      if (last && last.year === mo.year) {
        last.widthPx = mo.startX + mo.widthPx - last.startX;
      } else {
        bands.push({ year: mo.year, startX: mo.startX, widthPx: mo.widthPx });
      }
    }
    return bands;
  }, [months]);

  // ───── Today / scroll-to-today ─────
  const todayX = Math.round((+today - +rangeStart) / 86400_000) * DAY_W;
  const todayInRange = todayX >= 0 && todayX <= totalW;

  const jumpToToday = useCallback(() => {
    if (!scrollRef.current || !todayInRange) return;
    scrollRef.current.scrollTo({ left: Math.max(0, todayX - 280), behavior: "smooth" });
  }, [todayInRange, todayX]);

  useEffect(() => {
    if (!scrollRef.current || !todayInRange) return;
    scrollRef.current.scrollLeft = Math.max(0, todayX - 280);
    lastScrollLeft.current = scrollRef.current.scrollLeft;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart.getTime(), totalDays]);

  function dayOffset(d: string | null): number | null {
    if (!d) return null;
    return Math.round((+new Date(d) - +rangeStart) / 86400_000);
  }

  // ───── Auto vertical-scroll on horizontal scroll ─────
  // Whenever the user (or "Jump to Today") changes scrollLeft, we
  // vertically scroll so the topmost visible row is the first milestone
  // whose dueDate is on or after the visible left-edge date. Past
  // milestones scroll up out of view; future ones come down into view.
  // Auto-track is always on — we don't try to "lock it out" when the
  // user vertical-scrolls, because that detection had feedback loops
  // with our own programmatic scrolls. The user CAN still vertical
  // scroll freely; their position will simply be re-synced on the next
  // horizontal scroll. If they want to stay on a specific row, they
  // can stop horizontally scrolling.
  const triggerProgrammatic = (mutator: () => void) => {
    isProgrammaticScroll.current = true;
    mutator();
    if (programmaticTimer.current != null) window.clearTimeout(programmaticTimer.current);
    programmaticTimer.current = window.setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 80);
  };

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (isProgrammaticScroll.current) return; // ignore our own programmatic scrollTop change
    const sl = el.scrollLeft;
    if (sl === lastScrollLeft.current) return; // pure vertical-only scroll — leave alone
    lastScrollLeft.current = sl;

    const visibleLeftDay = sl / DAY_W;
    const idx = sortedMs.findIndex((m) => {
      const due = dayOffset(m.dueDate);
      return due != null && due >= visibleLeftDay;
    });
    if (idx > 0) {
      const targetTop = (idx - 1) * ROW_H; // row entering the viewport sits at the top
      if (Math.abs(el.scrollTop - targetTop) > 4) {
        triggerProgrammatic(() => { el.scrollTop = targetTop; });
      }
    } else if (idx === 0 || idx === -1) {
      // Either everything is upcoming (idx 0) or everything is past (idx -1).
      // In both cases we want scrollTop at 0 (no rows pushed out).
      if (el.scrollTop !== 0) {
        triggerProgrammatic(() => { el.scrollTop = 0; });
      }
    }
  };

  const handleJumpToToday = () => {
    if (scrollRef.current) {
      triggerProgrammatic(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      });
    }
    jumpToToday();
  };

  const quickUpdate = (id: string, field: string, val: any) =>
    updateMs.mutate({ milestoneId: id, [field]: val });

  // ───── Render ─────
  return (
    <div className="border rounded-lg overflow-hidden bg-card">
      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-muted/40">
        <div>
          <h3 className="font-semibold">Project Timeline</h3>
          <p className="text-xs text-muted-foreground">
            {milestones.length} milestone{milestones.length === 1 ? "" : "s"} · scroll horizontally to navigate
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleJumpToToday}>
          Jump to Today
        </Button>
      </div>

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="relative overflow-auto"
        style={{
          maxHeight: VIEWPORT_MAX,
          scrollBehavior: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{ display: "flex", minWidth: LABEL_W + totalW, position: "relative" }}>
          {/* Sticky left column */}
          <div
            style={{
              width: LABEL_W,
              minWidth: LABEL_W,
              position: "sticky",
              left: 0,
              zIndex: 3,
              background: "hsl(var(--card))",
              borderRight: "1px solid hsl(var(--border))",
              boxShadow: "8px 0 16px -12px rgba(15,23,42,0.08)",
            }}
          >
            <div
              style={{
                position: "sticky",
                top: 0,
                zIndex: 4,
                height: HEADER_H,
                background: "hsl(var(--muted) / 0.6)",
                borderBottom: "2px solid hsl(var(--primary))",
                display: "flex",
                alignItems: "flex-end",
                padding: "0 16px 10px",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "hsl(var(--foreground))",
              }}
            >
              Milestone
            </div>

            {sortedMs.map((m, idx) => {
              const isComplete = (m.progress ?? 0) >= 100;
              const isInFlight = (m.progress ?? 0) > 0 && (m.progress ?? 0) < 100;
              const statusColor = isComplete
                ? "hsl(152 50% 38%)"
                : isInFlight
                ? "hsl(var(--primary))"
                : "hsl(var(--muted-foreground))";
              const statusLabel = isComplete ? "Complete" : isInFlight ? "In progress" : "Upcoming";
              const isHovered = hoverId === m.id;
              return (
                <div
                  key={m.id}
                  onMouseEnter={() => setHoverId(m.id)}
                  onMouseLeave={() => setHoverId(null)}
                  style={{
                    height: ROW_H,
                    borderBottom:
                      idx === sortedMs.length - 1 ? "none" : "1px solid hsl(var(--border) / 0.5)",
                    padding: "0 16px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 4,
                    background: isComplete
                      ? "hsl(152 50% 38% / 0.04)"
                      : isHovered
                      ? "hsl(var(--primary) / 0.08)"
                      : idx % 2 === 1
                      ? "hsl(var(--muted) / 0.3)"
                      : "transparent",
                    transition: "background 150ms",
                  }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isComplete ? (
                      <span
                        aria-label="Complete"
                        style={{
                          width: 16, height: 16, borderRadius: "50%",
                          background: "hsl(152 50% 38%)",
                          color: "white", display: "inline-flex",
                          alignItems: "center", justifyContent: "center",
                          fontSize: 10, fontWeight: 800, flexShrink: 0,
                        }}
                      >
                        ✓
                      </span>
                    ) : (
                      <span
                        aria-hidden
                        style={{
                          width: 8, height: 8, borderRadius: "50%",
                          background: statusColor, flexShrink: 0,
                          marginLeft: 4, marginRight: 4,
                          boxShadow: isInFlight ? "0 0 0 3px hsl(var(--primary) / 0.18)" : "none",
                        }}
                      />
                    )}
                    <span
                      style={{
                        fontSize: 13.5, fontWeight: 600,
                        color: "hsl(var(--foreground))",
                        textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap",
                        letterSpacing: "-0.01em",
                        textDecoration: isComplete ? "line-through" : "none",
                        textDecorationColor: "hsl(152 50% 38% / 0.4)",
                      }}
                      title={m.name}
                    >
                      {m.name}
                    </span>
                  </div>
                  <div
                    className="flex items-center gap-2"
                    style={{ fontSize: 10.5, paddingLeft: 24, letterSpacing: "0.04em", textTransform: "uppercase", fontVariantNumeric: "tabular-nums" }}
                  >
                    <span style={{ color: "hsl(var(--muted-foreground))" }}>
                      {m.dueDate
                        ? new Date(m.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })
                        : "No date"}
                    </span>
                    <span style={{ color: "hsl(var(--border))" }}>·</span>
                    <span style={{ color: statusColor, fontWeight: 700 }}>{statusLabel}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right scrollable timeline */}
          <div style={{ position: "relative", width: totalW, minWidth: totalW }}>
            {/* Two-row sticky header */}
            <div
              style={{
                position: "sticky", top: 0, zIndex: 2, height: HEADER_H,
                background: "hsl(var(--muted) / 0.6)",
                borderBottom: "2px solid hsl(var(--primary))",
              }}
            >
              {yearBands.map((b) => (
                <div
                  key={`y-${b.year}-${b.startX}`}
                  style={{
                    position: "absolute", left: b.startX, width: b.widthPx,
                    top: 0, height: HEADER_YEAR_H,
                    borderRight: "1px solid hsl(var(--border))",
                    display: "flex", alignItems: "center", padding: "0 14px",
                    fontSize: 10.5, fontWeight: 700,
                    color: "hsl(var(--primary))",
                    letterSpacing: "0.14em", textTransform: "uppercase",
                    background: "hsl(var(--card))",
                  }}
                >
                  {b.year}
                </div>
              ))}
              {months.map((mo, i) => (
                <div
                  key={`m-${mo.startX}-${i}`}
                  style={{
                    position: "absolute", left: mo.startX, width: mo.widthPx,
                    top: HEADER_YEAR_H, height: HEADER_MONTH_H,
                    borderRight: "1px solid hsl(var(--border))",
                    display: "flex", alignItems: "center", padding: "0 14px",
                    fontSize: 13, fontWeight: 600,
                    color: "hsl(var(--foreground))",
                    letterSpacing: "-0.005em",
                  }}
                >
                  {mo.label}
                </div>
              ))}
            </div>

            {/* Vertical month gridlines */}
            {months.map((mo, i) => (
              <div
                key={`g-${i}`}
                aria-hidden
                style={{
                  position: "absolute", left: mo.startX,
                  top: HEADER_H, bottom: 0, width: 1,
                  background: "hsl(var(--border) / 0.5)",
                  pointerEvents: "none",
                }}
              />
            ))}

            {/* Today rule */}
            {todayInRange && (
              <>
                <div
                  aria-hidden
                  style={{
                    position: "absolute", left: todayX, top: HEADER_H, bottom: 0,
                    width: 2, background: "hsl(var(--primary))", zIndex: 1,
                    pointerEvents: "none", boxShadow: "0 0 0 1px hsl(var(--primary) / 0.15)",
                  }}
                />
                <div
                  style={{
                    position: "absolute", top: HEADER_H - 12, left: todayX - 22,
                    background: "hsl(var(--primary))", color: "white",
                    fontSize: 9.5, fontWeight: 800, letterSpacing: "0.12em",
                    textTransform: "uppercase", padding: "3px 7px", borderRadius: 3,
                    zIndex: 2, pointerEvents: "none",
                  }}
                >
                  Today
                </div>
              </>
            )}

            {/* Milestone rows */}
            {sortedMs.map((m, idx) => {
              const startOff = dayOffset(m.startDate ?? m.dueDate);
              const endOff = dayOffset(m.dueDate);
              const isComplete = (m.progress ?? 0) >= 100;
              const isInFlight = (m.progress ?? 0) > 0 && (m.progress ?? 0) < 100;
              const progress = Math.max(0, Math.min(100, m.progress ?? 0));
              const isHovered = hoverId === m.id;
              const rowBg = isComplete
                ? "hsl(152 50% 38% / 0.04)"
                : isHovered
                ? "hsl(var(--primary) / 0.08)"
                : idx % 2 === 1
                ? "hsl(var(--muted) / 0.3)"
                : "transparent";

              if (startOff == null && endOff == null) {
                return (
                  <div
                    key={m.id}
                    onMouseEnter={() => setHoverId(m.id)}
                    onMouseLeave={() => setHoverId(null)}
                    style={{
                      height: ROW_H,
                      borderBottom: idx === sortedMs.length - 1 ? "none" : "1px solid hsl(var(--border) / 0.5)",
                      background: rowBg,
                      transition: "background 150ms",
                    }}
                  />
                );
              }

              const trackStart = Math.min(startOff ?? endOff!, endOff ?? startOff!);
              const trackEnd = Math.max(startOff ?? endOff!, endOff ?? startOff!);
              const trackX = trackStart * DAY_W;
              const trackW = Math.max(DAY_W, (trackEnd - trackStart + 1) * DAY_W);
              const fillColor = isComplete ? "hsl(152 50% 38%)" : "hsl(var(--primary))";
              const trackBg = isComplete
                ? "hsl(152 50% 38% / 0.18)"
                : isInFlight
                ? "hsl(var(--primary) / 0.15)"
                : "hsl(var(--muted) / 0.5)";
              const trackBorder = isComplete
                ? "hsl(152 50% 38% / 0.4)"
                : isInFlight
                ? "hsl(var(--primary) / 0.45)"
                : "hsl(var(--border))";

              const barEl = (
                <div
                  onMouseEnter={() => setHoverId(m.id)}
                  onMouseLeave={() => setHoverId(null)}
                  onClick={clientFacing ? undefined : () => setEditingId(m.id)}
                  style={{
                    position: "relative",
                    height: ROW_H,
                    borderBottom: idx === sortedMs.length - 1 ? "none" : "1px solid hsl(var(--border) / 0.5)",
                    background: rowBg,
                    transition: "background 150ms",
                    cursor: clientFacing ? "default" : "pointer",
                  }}
                >
                  <div
                    style={{
                      position: "absolute", left: trackX, width: trackW,
                      top: "50%", transform: "translateY(-50%)",
                      height: 14, borderRadius: 4,
                      background: trackBg, border: `1px solid ${trackBorder}`,
                    }}
                  />
                  <div
                    style={{
                      position: "absolute", left: trackX,
                      width: Math.max(8, (trackW * progress) / 100),
                      top: "50%", transform: "translateY(-50%)",
                      height: 14, borderRadius: 4, background: fillColor,
                      boxShadow: isComplete
                        ? "inset 0 -3px 0 hsl(152 50% 38% / 0.5)"
                        : "inset 0 -3px 0 hsl(var(--primary) / 0.4)",
                    }}
                  />
                  {isInFlight && trackW > 64 && (
                    <span
                      style={{
                        position: "absolute", left: trackX + 10, top: "50%",
                        transform: "translateY(-50%)", fontSize: 10.5, fontWeight: 700,
                        color: "white", pointerEvents: "none",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {Math.round(progress)}%
                    </span>
                  )}
                  {isComplete && trackW > 28 && (
                    <span
                      style={{
                        position: "absolute", left: trackX + Math.max(8, trackW / 2 - 8),
                        top: "50%", transform: "translateY(-50%)",
                        color: "white", fontSize: 13, fontWeight: 800, pointerEvents: "none",
                      }}
                    >
                      ✓
                    </span>
                  )}
                </div>
              );

              if (clientFacing) return <div key={m.id}>{barEl}</div>;

              return (
                <Popover
                  key={m.id}
                  open={editingId === m.id}
                  onOpenChange={(open) => {
                    setEditingId(open ? m.id : null);
                    if (!open) setLocalProg(null);
                  }}
                >
                  <PopoverTrigger asChild>{barEl}</PopoverTrigger>
                  <PopoverContent className="w-72 p-0" side="bottom" align="start" sideOffset={8}>
                    <div className="p-3 border-b bg-muted/30">
                      <p className="font-semibold text-sm">{m.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(() => {
                          if (!m.dueDate) return null;
                          const due = new Date(m.dueDate);
                          const start = m.startDate ? new Date(m.startDate) : due;
                          const days = Math.max(1, Math.round((+due - +start) / 864e5) + 1);
                          const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
                          const sameDay = +start === +due;
                          return (
                            <>
                              {sameDay
                                ? due.toLocaleDateString("en-US", opts)
                                : `${start.toLocaleDateString("en-US", opts)} – ${due.toLocaleDateString("en-US", opts)}`}
                              {` · ${days}d`}
                              {m.owner && ` · ${m.owner}`}
                            </>
                          );
                        })()}
                      </p>
                    </div>
                    <div className="p-3 space-y-3">
                      {(() => {
                        const dp = localProg !== null ? localProg : (m.progress || 0);
                        return (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-xs">Progress: {dp}%</Label>
                              <div className="flex gap-1">
                                {dp < 100 ? (
                                  <Button
                                    size="sm"
                                    className="h-6 text-xs gap-1 bg-green-600 hover:bg-green-700 text-white"
                                    onClick={() => { setLocalProg(null); quickUpdate(m.id, "progress", 100); }}
                                  >
                                    <Check className="w-3 h-3" /> Complete
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm" variant="outline"
                                    className="h-6 text-xs gap-1"
                                    onClick={() => { setLocalProg(null); quickUpdate(m.id, "progress", 0); }}
                                  >
                                    <X className="w-3 h-3" /> Reopen
                                  </Button>
                                )}
                              </div>
                            </div>
                            <Slider
                              value={[dp]}
                              onValueChange={([v]) => setLocalProg(v)}
                              onValueCommit={([v]) => { setLocalProg(null); quickUpdate(m.id, "progress", v); }}
                              min={0} max={100} step={5}
                            />
                          </div>
                        );
                      })()}
                      <div>
                        <Label className="text-xs">Priority</Label>
                        <Select value={m.priority || "Medium"} onValueChange={(v) => quickUpdate(m.id, "priority", v)}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="High">High</SelectItem>
                            <SelectItem value="Medium">Medium</SelectItem>
                            <SelectItem value="Low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Owner</Label>
                        <Select value={m.owner || "TEAM"} onValueChange={(v) => quickUpdate(m.id, "owner", v)}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {teamMembers.map((tm) => (
                              <SelectItem key={tm} value={tm}>{tm}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Due Date</Label>
                        <Input
                          type="date"
                          className="h-8 text-xs"
                          value={m.dueDate ? new Date(m.dueDate).toISOString().split("T")[0] : ""}
                          onChange={(e) => quickUpdate(m.id, "dueDate", new Date(e.target.value + "T12:00:00").toISOString())}
                        />
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
